import { useCallback, useEffect, useRef, useState } from 'react';

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import type {
  YardGateChecklistAttachmentDto,
  YardGateChecklistAttachmentsDto,
} from 'contracts/YardGate';
import type { YardGateEntry } from '../../adapter';
import * as S from '../../styles';

export const YARD_GATE_CHECKLIST_REQUIRED_ERROR =
  'Selecione ao menos um arquivo.';
export const YARD_GATE_CHECKLIST_LIMIT_ERROR =
  'É permitido manter no máximo 3 anexos.';
export const YARD_GATE_CHECKLIST_TYPE_ERROR =
  'Envie apenas arquivos PDF ou imagens (png/jpg).';
export const YARD_GATE_CHECKLIST_SIZE_ERROR =
  'Cada arquivo deve ter no máximo 10MB.';

const ACCEPTED_EXTENSIONS = ['pdf', 'png', 'jpg', 'jpeg'];
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_ATTACHMENTS = 3;

const fileExtension = (file: File): string =>
  file.name.split('.').pop()?.toLowerCase() ?? '';

const uploadedAtLabel = (uploadedAt: string): string => {
  const date = new Date(uploadedAt);
  return Number.isNaN(date.getTime())
    ? uploadedAt
    : date.toLocaleString('pt-BR');
};

interface ChecklistModalProps {
  entry: YardGateEntry;
  isBusy: boolean;
  onClose: () => void;
  onLoad: (entry: YardGateEntry) => Promise<YardGateChecklistAttachmentsDto>;
  onUpload: (entry: YardGateEntry, files: File[]) => Promise<boolean>;
  onOpen: (
    entry: YardGateEntry,
    attachment: YardGateChecklistAttachmentDto
  ) => Promise<boolean>;
  onDelete: (
    entry: YardGateEntry,
    attachment: YardGateChecklistAttachmentDto
  ) => Promise<boolean>;
}

export const ChecklistModal = ({
  entry,
  isBusy,
  onClose,
  onLoad,
  onUpload,
  onOpen,
  onDelete,
}: ChecklistModalProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [attachments, setAttachments] = useState<
    YardGateChecklistAttachmentDto[]
  >([]);
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState('');
  const [listError, setListError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const loadAttachments = useCallback(async () => {
    setIsLoading(true);
    setListError('');
    try {
      const response = await onLoad(entry);
      setAttachments(response.attachments);
    } catch {
      setListError('Não foi possível carregar os anexos.');
    } finally {
      setIsLoading(false);
    }
  }, [entry, onLoad]);

  useEffect(() => {
    setFiles([]);
    setError('');
    setConfirmDeleteId(null);
    if (inputRef.current) inputRef.current.value = '';
    void loadAttachments();
  }, [entry.id, loadAttachments]);

  const save = async () => {
    if (isBusy) return;
    if (!files.length) {
      setError(YARD_GATE_CHECKLIST_REQUIRED_ERROR);
      return;
    }
    if (attachments.length + files.length > MAX_ATTACHMENTS) {
      setError(YARD_GATE_CHECKLIST_LIMIT_ERROR);
      return;
    }
    if (
      files.some((file) => !ACCEPTED_EXTENSIONS.includes(fileExtension(file)))
    ) {
      setError(YARD_GATE_CHECKLIST_TYPE_ERROR);
      return;
    }
    if (files.some((file) => file.size > MAX_FILE_SIZE)) {
      setError(YARD_GATE_CHECKLIST_SIZE_ERROR);
      return;
    }

    const saved = await onUpload(entry, files);
    if (!saved) return;

    setFiles([]);
    setError('');
    if (inputRef.current) inputRef.current.value = '';
    await loadAttachments();
  };

  const remove = async (attachment: YardGateChecklistAttachmentDto) => {
    if (isBusy) return;
    const deleted = await onDelete(entry, attachment);
    if (!deleted) return;
    setConfirmDeleteId(null);
    await loadAttachments();
  };

  const hasAvailableSlots = attachments.length < MAX_ATTACHMENTS;
  const fieldErrorId = `checklist-modal-${entry.id}-error`;

  return (
    <Dialog open onOpenChange={(open) => !open && !isBusy && onClose()}>
      <DialogContent
        aria-describedby={undefined}
        className="bg-white sm:max-w-[560px]"
      >
        <DialogHeader>
          <DialogTitle>Anexos</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <section aria-label="Anexos enviados" className="space-y-2">
            {isLoading ? (
              <p className="text-sm text-slate-600" role="status">
                Carregando anexos...
              </p>
            ) : listError ? (
              <div className="space-y-2" role="alert">
                <p className="text-sm text-red-600">{listError}</p>
                <S.Button
                  mood="light"
                  type="button"
                  onClick={() => void loadAttachments()}
                  disabled={isBusy}
                >
                  Tentar novamente
                </S.Button>
              </div>
            ) : attachments.length ? (
              <ul className="space-y-2">
                {attachments.map((attachment) => (
                  <li
                    key={attachment.id}
                    className="rounded-md border border-slate-200 p-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-700">
                          {attachment.fileName}
                        </p>
                        <p className="text-xs text-slate-500">
                          {uploadedAtLabel(attachment.uploadedAt)}
                        </p>
                      </div>
                      {confirmDeleteId === attachment.id ? (
                        <div className="flex shrink-0 items-center gap-2">
                          <span className="text-xs text-slate-600">
                            Excluir?
                          </span>
                          <button
                            type="button"
                            className="text-xs font-semibold text-slate-600 hover:underline"
                            onClick={() => setConfirmDeleteId(null)}
                            disabled={isBusy}
                          >
                            Cancelar
                          </button>
                          <button
                            type="button"
                            className="text-xs font-semibold text-red-600 hover:underline"
                            onClick={() => void remove(attachment)}
                            disabled={isBusy}
                            aria-label={`Confirmar exclusão de ${attachment.fileName}`}
                          >
                            Excluir
                          </button>
                        </div>
                      ) : (
                        <div className="flex shrink-0 items-center gap-3">
                          <button
                            type="button"
                            className="text-xs font-semibold text-[#EE7C2C] hover:underline"
                            onClick={() => void onOpen(entry, attachment)}
                            disabled={isBusy}
                            aria-label={`Abrir ${attachment.fileName}`}
                          >
                            Abrir
                          </button>
                          <button
                            type="button"
                            className="text-xs font-semibold text-red-600 hover:underline"
                            onClick={() => setConfirmDeleteId(attachment.id)}
                            disabled={isBusy}
                            aria-label={`Excluir ${attachment.fileName}`}
                          >
                            Excluir
                          </button>
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-slate-600">Nenhum anexo enviado.</p>
            )}
          </section>

          <S.Field>
            <div className="flex items-center justify-between gap-3">
              <S.Label htmlFor={`checklist-modal-${entry.id}`}>
                Adicionar arquivos
              </S.Label>
              <span className="text-xs text-slate-500">
                {attachments.length}/3
              </span>
            </div>
            <p className="text-xs text-slate-500">
              Até 3 arquivos no total. PDF, PNG ou JPG, com até 10MB cada.
            </p>
            <input
              ref={inputRef}
              id={`checklist-modal-${entry.id}`}
              type="file"
              multiple
              accept=".pdf,.png,.jpg,.jpeg"
              aria-label={`Arquivos do checklist de ${entry.plate}`}
              aria-describedby={error ? fieldErrorId : undefined}
              disabled={
                isBusy || isLoading || !!listError || !hasAvailableSlots
              }
              className="text-sm text-slate-700 file:mr-3 file:rounded-md file:border-0 file:bg-[#EE7C2C] file:px-3 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:brightness-95 disabled:opacity-60"
              onChange={(event) => {
                const selected = Array.from(event.target.files ?? []);
                if (attachments.length + selected.length > MAX_ATTACHMENTS) {
                  setFiles([]);
                  setError(YARD_GATE_CHECKLIST_LIMIT_ERROR);
                  event.target.value = '';
                  return;
                }
                setFiles(selected);
                setError('');
              }}
            />
            {files.length ? (
              <ul className="text-xs text-slate-600">
                {files.map((file) => (
                  <li key={`${file.name}-${file.size}`}>{file.name}</li>
                ))}
              </ul>
            ) : null}
            {error ? (
              <S.FieldError id={fieldErrorId}>{error}</S.FieldError>
            ) : null}
          </S.Field>
          <DialogFooter>
            <S.Button
              mood="light"
              type="button"
              onClick={onClose}
              disabled={isBusy}
            >
              Fechar
            </S.Button>
            <S.Button
              type="button"
              onClick={save}
              disabled={
                isBusy || isLoading || !!listError || !hasAvailableSlots
              }
            >
              Enviar{files.length ? ` (${files.length})` : ''}
            </S.Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};
